import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import QrCodeIcon from '@mui/icons-material/QrCode'
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { requestWithUserAuth } from '../api/auth'
import {
  activateRegistration,
  deactivateRegistration,
  fetchAllRegistrations,
  fetchOwnRegistration,
  fetchRegistrations
} from '../api/snacks'
import { fetchUserVotingCategory } from '../api/voting'
import { useThemeMode } from '../theme'
import { getEntitlements, isDefined } from '../utils'
import { AuthorizationGate } from './components/Authorization'
import { ApprovePendingDialog } from './components/snacks/ApprovePendingDialog'
import { COUNTRIES } from './components/snacks/countries'
import { QRDisplay } from './components/snacks/QRDisplay'
import { RegistrationCard } from './components/snacks/RegistrationCard'
import { RegistrationForm } from './components/snacks/RegistrationForm'
import { RegistrationList } from './components/snacks/RegistrationList'
import { QRScanner } from './components/snacks/QRScanner'
import { SnacksMenu } from './components/snacks/SnacksMenu'
import { SnacksPageHeader } from './components/snacks/SnacksPageHeader'

const makeStyles = (palette) => ({
  pageBox: { width: '100%', display: 'flex', flexDirection: 'column' },
  contentBox: { maxWidth: '700px', margin: '0 auto', padding: '0 8px', width: '100%', mt: 1 },
  loadingBox: { display: 'flex', justifyContent: 'center', py: 4 },
  spinner: { color: palette.headerColor },
  emptyPaper: { p: 4, textAlign: 'center' },
  emptyIcon: { fontSize: '3rem', color: palette.headerColor, opacity: 0.3, mb: 1 },
  pendingAlert: { mb: 2, mt: 1 },
  activeBox: { mb: 3 },
  qrBtnBox: { mt: 2, display: 'flex', justifyContent: 'center' },
  qrBtn: { borderRadius: '25px', color: palette.headerColor, borderColor: palette.headerColor },
  qrDialogPaper: { width: '90vw', maxWidth: '90vw', height: '90vh', maxHeight: '90vh', m: 1 },
  qrTitleBox: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  qrTitle: { color: palette.headerColor, fontWeight: 700 },
  qrContent: { display: 'flex', flexDirection: 'column', pt: 1 },
  qrInfoBox: { mb: 2 },
  qrFieldLabel: { color: palette.headerColor, fontWeight: 700, display: 'block' },
  qrFieldLabelAlt: { color: palette.headerColor, fontWeight: 700, display: 'block', mt: 1 },
  qrCardBox: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qrSizer: { width: '90%', maxWidth: '400px' }
})

const QRDialog = ({ open, onClose, registration, styles, t }) => (
  <Dialog open={open} onClose={onClose} PaperProps={{ sx: styles.qrDialogPaper }}>
    <DialogTitle sx={{ pb: 0 }}>
      <Box sx={styles.qrTitleBox}>
        <Typography variant="h5" sx={styles.qrTitle}>
          {t('snacks-table')} {registration?.table_number}
        </Typography>
        {registration?.country && (
          <ReactCountryFlag
            countryCode={registration.country}
            svg
            style={{ width: '2.5em', height: '2.5em', flexShrink: 0 }}
            title={COUNTRIES.find((c) => c.code === registration.country)?.name}
          />
        )}
      </Box>
    </DialogTitle>
    <DialogContent sx={styles.qrContent}>
      <Box sx={styles.qrInfoBox}>
        <Typography variant="caption" sx={styles.qrFieldLabel}>
          {t('snacks-field-nick')}:
        </Typography>
        <Typography variant="body1">{registration?.owner_nick}</Typography>
        {registration?.description && (
          <>
            <Typography variant="caption" sx={styles.qrFieldLabelAlt}>
              {t('snacks-field-description')}:
            </Typography>
            <Typography variant="body2">{registration.description}</Typography>
          </>
        )}
      </Box>
      <Box sx={styles.qrCardBox}>
        <Box sx={styles.qrSizer}>
          <QRDisplay size="100%" />
        </Box>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{t('cancel')}</Button>
    </DialogActions>
  </Dialog>
)

const ActivateErrorDialog = ({ error, onClose, t }) => (
  <Dialog open={Boolean(error)} onClose={onClose}>
    <DialogTitle sx={{ color: 'error.main' }}>{t('snacks-error-title')}</DialogTitle>
    <DialogContent>
      <DialogContentText>{error}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{t('cancel')}</Button>
    </DialogActions>
  </Dialog>
)

export const SnacksExchange = () => {
  const { t } = useTranslation()
  const { palette } = useThemeMode()
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)
  const { registrations, allRegistrations, ownRegistration, registrationsFetched } = useSelector((state) => state.snacks)
  const { pathname } = useLocation()
  const view = pathname === '/voting/snacks/my-table' ? 'my-table' : 'all'
  const [formMode, setFormMode] = useState(null)
  const [editTarget, setEditTarget] = useState(null)
  const [approveTarget, setApproveTarget] = useState(null)
  const [votesFilter, setVotesFilter] = useState('all')
  const [showQR, setShowQR] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [activateError, setActivateError] = useState(null)

  const votingData = useSelector((state) => state.voting)
  const snacksVotes = votingData?.snacks_exchange?.votes ?? {}

  const votingStart = useSelector((state) => state.voting?.snacks_exchange?.votingStart)
  const votingNotStarted = votingStart != null && votingStart > 0

  const entitlements = getEntitlements(auth?.accessToken)
  const isAdmin = isDefined(entitlements) && entitlements.includes('AdminCesfurSnacks:full')

  const styles = makeStyles(palette)

  useEffect(() => {
    requestWithUserAuth(config, auth, (freshAuth) => {
      const freshEntitlements = getEntitlements(freshAuth?.accessToken)
      const freshIsAdmin = isDefined(freshEntitlements) && freshEntitlements.includes('AdminCesfurSnacks:full')
      fetchOwnRegistration(config, freshAuth)
      fetchUserVotingCategory(config, freshAuth, 'snacks_exchange')
      if (freshIsAdmin) {
        fetchAllRegistrations(config, freshAuth)
      } else {
        fetchRegistrations(config, freshAuth)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.authToken, auth?.accessToken])

  const handleEdit = (registration) => {
    setEditTarget(registration)
    setFormMode('admin-edit')
  }

  const handleApprove = (registration) => {
    setApproveTarget(registration)
  }

  const handleApproveClose = () => {
    setApproveTarget(null)
  }

  const handleApproved = () => {
    setApproveTarget(null)
    requestWithUserAuth(config, auth, (a) => { fetchAllRegistrations(config, a) })
  }

  const handleDeactivate = (id) => {
    requestWithUserAuth(config, auth, (a) => {
      deactivateRegistration(config, a, id)
    })
  }

  const DETAIL_KEYS = {
    'User already has another active registration': 'snacks-error-already-active'
  }

  const handleActivate = (id) => {
    requestWithUserAuth(config, auth, (updatedAuth) => {
      activateRegistration(
        config, updatedAuth, id,
        () => { requestWithUserAuth(config, auth, (a) => { fetchAllRegistrations(config, a) }) },
        (error) => {
          const detail = error?.response?.data?.detail
          const key = detail ? DETAIL_KEYS[detail] : null
          setActivateError(key ? t(key) : (detail || t('snacks-error-generic')))
        },
        (error) => {
          const detail = error?.response?.data?.detail
          setActivateError(detail || t('snacks-error-generic'))
        }
      )
    })
  }

  const handleCloseForm = () => {
    setFormMode(null)
    setEditTarget(null)
    requestWithUserAuth(config, auth, (freshAuth) => {
      const freshEntitlements = getEntitlements(freshAuth?.accessToken)
      const freshIsAdmin = isDefined(freshEntitlements) && freshEntitlements.includes('AdminCesfurSnacks:full')
      fetchOwnRegistration(config, freshAuth)
      if (freshIsAdmin) {
        fetchAllRegistrations(config, freshAuth)
      } else {
        fetchRegistrations(config, freshAuth)
      }
    })
  }

  const sortByTableNumber = (regs) => [...regs].sort((a, b) => {
    if (a.table_number == null && b.table_number == null) return 0
    if (a.table_number == null) return 1
    if (b.table_number == null) return -1
    return a.table_number - b.table_number
  })

  const displayedRegistrations = sortByTableNumber(isAdmin ? allRegistrations : registrations)

  const filteredRegistrations = votesFilter === 'voted'
    ? displayedRegistrations.filter(
      (r) => r.table_number != null && snacksVotes[r.table_number]?.length > 0
    )
    : displayedRegistrations

  return (
    <AuthorizationGate>
      <Box sx={styles.pageBox}>
        <SnacksMenu />

        {view === 'all' && (
          <>
            <SnacksPageHeader
              showRegister={isAdmin}
              onRegister={() => setFormMode('admin-create')}
              votesFilter={votesFilter}
              onVotesFilterChange={setVotesFilter}
              showScanQR
              onScanQR={() => setScannerOpen(true)}
            />
            <Box sx={styles.contentBox}>
              {votingNotStarted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('snacks-voting-not-started')}
                </Alert>
              )}
              <RegistrationList
                registrations={filteredRegistrations}
                isAdmin={isAdmin}
                onEdit={handleEdit}
                onApprove={handleApprove}
                onDeactivate={handleDeactivate}
                onActivate={handleActivate}
                emptyMessage={votesFilter === 'voted' ? 'snacks-no-voted-tables' : 'snacks-no-registrations'}
              />
            </Box>
          </>
        )}

        {view === 'my-table' && (
          <>
            <SnacksPageHeader
              showRegister={registrationsFetched && ownRegistration === null}
              onRegister={() => setFormMode('user-register')}
              registerLabel="snacks-register-my-table"
              showEdit={registrationsFetched && ownRegistration !== null && ownRegistration.status !== 'disabled'}
              onEdit={() => setFormMode('user-edit')}
              hideVotesSummary
              showScanQR
              onScanQR={() => setScannerOpen(true)}
            />
            <Box sx={styles.contentBox}>
              {!registrationsFetched && (
                <Box sx={styles.loadingBox}>
                  <CircularProgress sx={styles.spinner} />
                </Box>
              )}

              {registrationsFetched && ownRegistration === null && (
                <Paper elevation={2} sx={styles.emptyPaper}>
                  <TableRestaurantIcon sx={styles.emptyIcon} />
                  <Typography variant="body2" color="text.secondary">
                    {t('snacks-no-own-registration')}
                  </Typography>
                </Paper>
              )}

              {registrationsFetched && ownRegistration !== null && (
                <Paper elevation={2} sx={{ p: 2 }}>
                  {ownRegistration?.status === 'pending' && (
                    <>
                      <Alert icon={<InfoOutlinedIcon />} severity="info" sx={styles.pendingAlert}>
                        {t('snacks-pending-approval')}
                      </Alert>
                      <RegistrationCard registration={ownRegistration} isAdmin={false} isOwn flat />
                    </>
                  )}

                  {ownRegistration?.status === 'active' && (
                    <Box sx={styles.activeBox}>
                      <RegistrationCard registration={ownRegistration} isAdmin={false} isOwn flat />
                      <Box sx={styles.qrBtnBox}>
                        <Button
                          variant="outlined"
                          startIcon={<QrCodeIcon />}
                          onClick={() => setShowQR(true)}
                          sx={styles.qrBtn}
                        >
                          {t('snacks-show-qr')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </>
        )}

        {formMode && (
          <RegistrationForm
            mode={formMode}
            registration={formMode === 'user-edit' ? ownRegistration : editTarget}
            onClose={handleCloseForm}
          />
        )}

        {approveTarget && (
          <ApprovePendingDialog
            registration={approveTarget}
            onClose={handleApproveClose}
            onApproved={handleApproved}
          />
        )}

        <QRDialog
          open={showQR}
          onClose={() => setShowQR(false)}
          registration={ownRegistration}
          styles={styles}
          t={t}
        />

        <ActivateErrorDialog
          error={activateError}
          onClose={() => setActivateError(null)}
          t={t}
        />

        <QRScanner open={scannerOpen} onClose={() => setScannerOpen(false)} />
      </Box>
    </AuthorizationGate>
  )
}
